import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-toastify';
import { tenantApi } from '../lib/tenant-api';
import type { RedactionSettingsRequest } from '../lib/api-types';
import { PageLoader } from './common';

const QUERY_KEY = ['redactionSettings'] as const;

interface ToggleRowProps {
  label: string;
  description: string;
  checked: boolean;
  disabled: boolean;
  onChange?: (next: boolean) => void;
}

function ToggleRow({ label, description, checked, disabled, onChange }: ToggleRowProps) {
  return (
    <div className="flex items-center justify-between rounded-md border border-gray-200 bg-white px-4 py-3">
      <div className="mr-4">
        <p className="text-sm font-medium text-gray-900">{label}</p>
        <p className="text-xs text-gray-500">{description}</p>
      </div>
      <button
        type="button"
        role="switch"
        aria-checked={checked}
        disabled={disabled}
        onClick={() => onChange?.(!checked)}
        className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 ${checked ? 'bg-indigo-600' : 'bg-gray-200'}`}
      >
        <span
          className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${checked ? 'translate-x-5' : 'translate-x-0'}`}
        />
      </button>
    </div>
  );
}

const CHILD_ROWS: {
  key: keyof Omit<RedactionSettingsRequest, 'enabled'>;
  label: string;
  description: string;
}[] = [
    {
      key: 'redactSsn',
      label: 'Social Security Numbers (SSN)',
      description: 'Masks 9-digit SSNs with or without dashes.',
    },
    {
      key: 'redactEmail',
      label: 'Email Addresses',
      description: 'Masks any email address pattern.',
    },
    {
      key: 'redactPhone',
      label: 'Phone Numbers',
      description: 'Masks US-format phone numbers.',
    },
    {
      key: 'redactDob',
      label: 'Dates of Birth',
      description: 'Masks MM/DD/YYYY and similar date patterns.',
    },
    {
      key: 'redactCreditCard',
      label: 'Credit Card Numbers',
      description: 'Masks Visa, Mastercard, Amex, Discover, and JCB numbers.',
    },
    {
      key: 'redactAccountNumber',
      label: 'Account / Member Numbers',
      description: 'Masks numeric IDs prefixed by acct, account, id, or member.',
    },
    {
      key: 'redactIpAddress',
      label: 'IP Addresses',
      description: 'Masks IPv4 addresses.',
    },
  ];

export default function RedactionSettingsSection() {
  const queryClient = useQueryClient();

  const { data, isLoading, isError } = useQuery({
    queryKey: QUERY_KEY,
    queryFn: tenantApi.getRedactionSettings,
  });

  const saveMutation = useMutation({
    mutationFn: tenantApi.updateRedactionSettings,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEY });
      toast.success('Redaction settings saved.');
    },
    onError: (err: unknown) => {
      const msg = err instanceof Error ? err.message : 'Failed to save settings.';
      toast.error(msg);
    },
  });

  /** Merge a single field update into the current settings and save */
  const patch = (partial: Partial<RedactionSettingsRequest>) => {
    if (!data) return;
    const next: RedactionSettingsRequest = {
      enabled: data.enabled,
      redactSsn: data.redactSsn,
      redactEmail: data.redactEmail,
      redactPhone: data.redactPhone,
      redactDob: data.redactDob,
      redactCreditCard: data.redactCreditCard,
      redactAccountNumber: data.redactAccountNumber,
      redactIpAddress: data.redactIpAddress,
      ...partial,
    };
    saveMutation.mutate(next);
  };

  const busy = isLoading || saveMutation.isPending;

  return (
    <div>
      <div className="sm:flex sm:items-center sm:justify-between">
        <div className="sm:flex-auto">
          <h1 className="text-lg font-bold leading-7 text-gray-900 sm:truncate sm:tracking-tight">
            Message Redaction
          </h1>
          <p className="mt-1 text-sm text-gray-700">
            Automatically redact sensitive data from inbound user messages before they reach the AI.
            Only active patterns are applied.
          </p>
        </div>
      </div>

      <div className="mt-8 space-y-4">
        {/* Loading state */}
        {isLoading && (
          <div className="flex items-center justify-center bg-gray-50">
            <PageLoader fullScreen={false} />
          </div>
        )
        }

        {/* Error / no-data state */}
        {!isLoading && (isError || !data) && (
          <ToggleRow
            label="Enable message redaction"
            description="Could not load settings."
            checked={false}
            disabled={true}
          />
        )}

        {/* Normal state */}
        {!isLoading && !isError && data && (
          <>
            <ToggleRow
              label="Enable message redaction"
              description="Master switch - turn off to disable all redaction for this tenant."
              checked={data.enabled}
              disabled={busy}
              onChange={(next) => patch({ enabled: next })}
            />

            {data.enabled && (
              <div className="ml-4 space-y-3 pl-4">
                {CHILD_ROWS.map((row) => (
                  <ToggleRow
                    key={row.key}
                    label={row.label}
                    description={row.description}
                    checked={data[row.key] as boolean}
                    disabled={busy}
                    onChange={(next) => patch({ [row.key]: next })}
                  />
                ))}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
